/**
 * Toast host, ported from the prototype's `.toast-host` / `.toast`.
 *
 * The host is a polite live region so a confirmation is announced without stealing focus, and a
 * toast never carries the only copy of an error: command failures are also rendered in place, so
 * an operator who misses the toast still sees what happened.
 */
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export type ToastTone = 'ok' | 'error';

interface ToastRecord {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastApi {
  /** Short confirmation of something that succeeded. */
  ok: (message: string) => void;
  /** Short failure note; the calling screen still renders the detail. */
  error: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const TOAST_MS = 4200;
const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);

  const push = useCallback((message: string, tone: ToastTone) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, message, tone }].slice(-MAX_VISIBLE));
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, TOAST_MS);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      ok: (message: string) => push(message, 'ok'),
      error: (message: string) => push(message, 'error'),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-host" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div className="toast" key={toast.id}>
            <i aria-hidden="true" style={toast.tone === 'error' ? { background: 'var(--red)' } : undefined} />
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}
